import AccessTime from '@mui/icons-material/AccessTime'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { toast } from 'react-toastify'
import {
  CustomSelect,
  selectTypes,
  TimeRangePicker,
} from 'src/consumer/components/helperComponents'
import { genericHandleChange } from 'src/consumer/helpers/utilFunctions'
import {
  getDataMapFromCommonResponse,
  getMessageMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
  getImageLink,
} from 'src/mentor/helpers/utilityFunctions'
import { postSPData } from 'src/supportPortal/services/services'
import {
  disableRosterSlot,
  getRosterPractitionerAvailability,
  updateRosterSlot,
} from 'src/supportPortal/services/rosterManagementService'

const AVAILABILITY_STATUS = {
  available: {
    label: 'Fully available',
    color: 'success',
    icon: CheckCircleIcon,
    tone: '#2e7d32',
  },
  partial: {
    label: 'Partially available',
    color: 'warning',
    icon: WarningAmberIcon,
    tone: '#ed6c02',
  },
  unavailable: {
    label: 'Unavailable',
    color: 'error',
    icon: CancelIcon,
    tone: '#d32f2f',
  },
}

function getAvailabilityConfig(availability) {
  if (!availability) {
    return {
      label: 'Checking availability',
      color: 'default',
      icon: InfoOutlinedIcon,
      tone: '#6b7280',
    }
  }
  return AVAILABILITY_STATUS[availability.availabilityStatus] || AVAILABILITY_STATUS.unavailable
}

function formatEventTypeLabel(value) {
  if (!value) return 'Event type not set'
  return String(value)
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function formatSlotLabel(item) {
  const date = item?.requestedDate || item?.date
  const start = item?.requestedStartTime || item?.startTime
  const end = item?.requestedEndTime || item?.endTime
  const dateLabel = date ? dayjs(date).format('ddd, D MMM YYYY') : null
  const timeLabel = start && end ? `${start}-${end}` : null
  return [dateLabel, timeLabel].filter(Boolean).join(' | ')
}

function joinVisible(parts, separator = ' / ') {
  return parts.filter(Boolean).join(separator)
}

function getAvailabilityFromResponse(resp) {
  return (
    resp?.data?.data?.['roster-practitioner-availability'] ||
    resp?.response?.data?.data?.['roster-practitioner-availability'] ||
    null
  )
}

function getAssignmentConflictFromResponse(resp) {
  return (
    resp?.data?.data?.['roster-assignment-conflict'] ||
    resp?.response?.data?.data?.['roster-assignment-conflict'] ||
    null
  )
}

function getResponseMessage(resp) {
  return resp?.data?.message || resp?.response?.data?.message || 'Could not save roster slot.'
}

export default function RosterForm({
  selectedSlot,
  selectedItem: _selectedItem,
  legendItems,
  getSidebarLegendValue,
  getSidebarLegendLabel,
  updateSlotOnCalendar,
  addEventToSlotList,
  onRosterSlotChanged = null,
  slotSaveApiSignature = '/api/secure/cc/support/roster/assign-therapist-to-chamber',
  dropDownKey = 'mentorId',
  dropDownLabel = 'Mentor',
  closePopup,
  transformToSaveItem = ({ rosterSaveItem, allSidebarItems: _allSidebarItems }) => {
    return rosterSaveItem
  },
  rosterSchedule = null, // Roster schedule for the room/date
  getRosterScheduleForChamberRoom = null,
  getLegendItemsForRosterSlot = null,
}) {
  const [rosterSlot, setRosterSlot] = useState()
  const currentDateInDayJs = dayjs(rosterSlot?.customDate)
  const [saving, setSaving] = useState(false)
  const [availabilityByMentorId, setAvailabilityByMentorId] = useState({})
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [availabilityError, setAvailabilityError] = useState('')
  const [availabilityDetail, setAvailabilityDetail] = useState(null)
  const [availabilityRefreshCounter, setAvailabilityRefreshCounter] = useState(0)
  const [editingRosterSlot, setEditingRosterSlot] = useState(null)
  const [editingRosterSchedule, setEditingRosterSchedule] = useState(null)
  const [editingLegendItems, setEditingLegendItems] = useState(null)
  const [slotActionLoadingId, setSlotActionLoadingId] = useState(null)
  const effectiveRosterSchedule = editingRosterSchedule || rosterSchedule
  const effectiveLegendItems = editingLegendItems || legendItems
  
  // Generate time options based on roster schedule
  const timeOptions = useMemo(() => {
    const valueFormat = 'HH:mm'
    const labelFormat = 'HH:mm'
    const options = []
    
    if (!effectiveRosterSchedule || !effectiveRosterSchedule.weeklyHourMap) {
      // Fallback: generate all 15-minute intervals for the day
      let time = dayjs().startOf('day')
      while (time.isBefore(dayjs().endOf('day'))) {
        options.push({ value: time.format(valueFormat), label: time.format(labelFormat) })
        time = time.add(15, 'minute')
      }
      return options
    }
    
    // Get day of week (monday, tuesday, etc.)
    const dayOfWeek = currentDateInDayJs.format('dddd').toLowerCase()
    const dayHours = effectiveRosterSchedule.weeklyHourMap[dayOfWeek] || []
    
    if (dayHours.length === 0) {
      // No hours for this day
      return []
    }
    
    // Generate time slots for all time ranges on this day
    dayHours.forEach((timeRange) => {
      if (!timeRange.start || !timeRange.end) return
      
      let time = dayjs(timeRange.start, 'HH:mm')
      const endTime = dayjs(timeRange.end, 'HH:mm')
      
      // Generate 15-minute intervals from start to end
      while (time.isBefore(endTime)) {
        options.push({ value: time.format(valueFormat), label: time.format(labelFormat) })
        time = time.add(15, 'minute')
      }
      
      // Always include the end time, even if it's not on a 15-minute boundary
      // This allows slots to end exactly at the roster schedule end time
      // Only add if it wasn't already added (i.e., if end time is not on a 15-minute boundary)
      if (!time.isSame(endTime)) {
        options.push({ value: endTime.format(valueFormat), label: endTime.format(labelFormat) })
      }
    })
    
    // Remove duplicates and sort
    const uniqueOptions = Array.from(
      new Map(options.map((opt) => [opt.value, opt])).values()
    ).sort((a, b) => a.value.localeCompare(b.value))
    
    return uniqueOptions
  }, [effectiveRosterSchedule, currentDateInDayJs])
  
  // Get "to" time options - start from the next slot after "from", including end time
  const toTimeOptions = useMemo(() => {
    if (!rosterSlot?.slotStartTime || !timeOptions.length) {
      return timeOptions
    }
    
    const startTime = dayjs(rosterSlot.slotStartTime, 'HH:mm')
    
    // Filter to only show times after the start time (including end time of schedule)
    // This allows selecting the end time of the roster schedule
    return timeOptions.filter((opt) => {
      const optTime = dayjs(opt.value, 'HH:mm')
      return optTime.isAfter(startTime)
    })
  }, [timeOptions, rosterSlot?.slotStartTime])
  
  // Validate custom time input - check if it's within roster schedule
  const validateCustomTime = useCallback((time, field) => {
    if (!time) {
      return false
    }
    
    // Check if time is valid format (HH:mm)
    const timeObj = dayjs(time, 'HH:mm', true)
    if (!timeObj.isValid()) {
      return false
    }
    
    if (!effectiveRosterSchedule || !effectiveRosterSchedule.weeklyHourMap) {
      // No schedule constraint - allow any valid time
      return true
    }
    
    // Get day of week
    const dayOfWeek = currentDateInDayJs.format('dddd').toLowerCase()
    const dayHours = effectiveRosterSchedule.weeklyHourMap[dayOfWeek] || []
    
    if (dayHours.length === 0) {
      return false // No hours for this day
    }
    
    // Check if time falls within any of the time ranges for this day
    const timeInMinutes = timeObj.hour() * 60 + timeObj.minute()
    
    const isValid = dayHours.some((timeRange) => {
      if (!timeRange.start || !timeRange.end) return false
      
      const rangeStart = dayjs(timeRange.start, 'HH:mm')
      const rangeEnd = dayjs(timeRange.end, 'HH:mm')
      const rangeStartMinutes = rangeStart.hour() * 60 + rangeStart.minute()
      const rangeEndMinutes = rangeEnd.hour() * 60 + rangeEnd.minute()
      
      // Time must be within the range
      // For start: >= start and < end (can start at opening, but must end before closing)
      // For end: > start and <= end (must be after start, can end at or before closing)
      if (field === 'start') {
        return timeInMinutes >= rangeStartMinutes && timeInMinutes < rangeEndMinutes
      } else {
        // For end time, allow up to and including the end time
        return timeInMinutes > rangeStartMinutes && timeInMinutes <= rangeEndMinutes
      }
    })
    
    return isValid
  }, [effectiveRosterSchedule, currentDateInDayJs])
  const selectedAvailability = useMemo(() => {
    if (!rosterSlot?.[dropDownKey]) {
      return null
    }
    return availabilityByMentorId[rosterSlot[dropDownKey]] || null
  }, [availabilityByMentorId, dropDownKey, rosterSlot])

  const enrichedLegendItems = useMemo(() => {
    return (effectiveLegendItems || []).map((item) => {
      const itemId = getSidebarLegendValue(item)
      return {
        ...item,
        availability: availabilityByMentorId[itemId] || null,
      }
    })
  }, [availabilityByMentorId, effectiveLegendItems, getSidebarLegendValue])

  const isSelectedPractitionerBlocked = !!selectedAvailability && selectedAvailability.fullyAvailable === false

  const isRosterSlotValid = useMemo(() => {
    if (!rosterSlot) {
      return false
    }
    if (!rosterSlot?.mentorId) {
      return false
    }
    if (dropDownKey === 'mentorId' && !rosterSlot?.eventType) {
      return false
    }
    if (availabilityLoading || isSelectedPractitionerBlocked) {
      return false
    }
    return true
  }, [availabilityLoading, dropDownKey, isSelectedPractitionerBlocked, rosterSlot])

  async function saveSlot() {
    const payload = transformToSaveItem({ rosterSaveItem: rosterSlot, allSidebarItems: effectiveLegendItems })
    if (!payload?.eventType) {
      toast.error('Event type is missing for this roster slot. Please reopen the event-type tab and try again.')
      return
    }
    setSaving(true)
    if (editingRosterSlot?.id) {
      payload.excludeRosterSlotId = editingRosterSlot.id
    }
    const resp = editingRosterSlot?.id
      ? await updateRosterSlot(editingRosterSlot.id, payload)
      : await postSPData(slotSaveApiSignature, payload)

    if (resp?.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const rosterItem = getDataMapFromCommonResponse(resp)?.['clinic-roster-slot']
        if (editingRosterSlot?.id) {
          onRosterSlotChanged?.(rosterItem, 'updated')
          toast.success('Roster slot updated.')
        } else {
          addEventToSlotList(rosterItem)
        }
        closePopup()
        return
      } else {
        toast.error(renderRosterAssignmentError(resp), {
          autoClose: 12000,
        })
      }
    } else {
      toast.error(renderRosterAssignmentError(resp), {
        autoClose: 12000,
      })
    }
    setSaving(false)
  }

  function beginEditRosterEngagement(engagement) {
    if (!engagement?.id) {
      toast.error('This roster slot cannot be edited because its slot id was not returned.')
      return
    }
    if (engagement?.metadata?.scheduleType === 'recurring') {
      toast.info('Recurring roster slots must be edited from the room schedule editor.')
      return
    }
    const editSlot = {
      id: engagement.id,
      clinicBrandId: engagement.clinicBrandId,
      clinicLocationId: engagement.clinicLocationId,
      chamberRoomId: engagement.chamberRoomId,
      mentorId: engagement.mentorId,
      eventType: engagement.eventType || rosterSlot?.eventType,
      eventSubType: engagement.eventSubType || rosterSlot?.eventSubType,
      customDate: engagement.date,
      slotStartTime: engagement.startTime,
      slotEndTime: engagement.endTime,
      excludeRosterSlotId: engagement.id,
    }
    setEditingRosterSlot(engagement)
    setEditingRosterSchedule(getRosterScheduleForChamberRoom?.(engagement.chamberRoomId) || null)
    const nextLegendItems = getLegendItemsForRosterSlot?.(editSlot)
    setEditingLegendItems(
      Array.isArray(nextLegendItems) && nextLegendItems.length > 0 ? nextLegendItems : null,
    )
    setRosterSlot((prev) => ({
      ...prev,
      ...editSlot,
      eventType: editSlot.eventType || prev?.eventType,
      eventSubType: editSlot.eventSubType || prev?.eventSubType,
    }))
    setAvailabilityDetail(null)
    setAvailabilityRefreshCounter((prev) => prev + 1)
  }

  function clearRosterSlotEdit() {
    setEditingRosterSlot(null)
    setEditingRosterSchedule(null)
    setEditingLegendItems(null)
    setRosterSlot(selectedSlot)
    setAvailabilityRefreshCounter((prev) => prev + 1)
  }

  async function cancelRosterEngagement(engagement) {
    if (!engagement?.id) {
      toast.error('This roster slot cannot be cancelled because its slot id was not returned.')
      return
    }
    setSlotActionLoadingId(engagement.id)
    const resp = await disableRosterSlot(engagement.id)
    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      const rosterItem = getDataMapFromCommonResponse(resp)?.['clinic-roster-slot'] || engagement
      onRosterSlotChanged?.(rosterItem, 'disabled')
      setAvailabilityDetail((prev) => {
        if (!prev) {
          return prev
        }
        return {
          ...prev,
          engagements: (prev.engagements || []).filter((item) => item?.id !== engagement.id),
        }
      })
      setAvailabilityRefreshCounter((prev) => prev + 1)
      toast.success('Roster slot cancelled.')
    } else {
      toast.error(getResponseMessage(resp) || 'Could not cancel roster slot.')
    }
    setSlotActionLoadingId(null)
  }

  function handleChangeDate([startTime, endTime]) {
    // If start time changed and end time is now invalid, reset end time to next slot
    let validEndTime = endTime
    if (startTime && endTime) {
      const start = dayjs(startTime, 'HH:mm')
      const end = dayjs(endTime, 'HH:mm')
      
      // If end time is before or equal to start time, set it to next slot
      if (!end.isAfter(start)) {
        validEndTime = start.add(15, 'minute').format('HH:mm')
      }
    } else if (startTime && !endTime) {
      // If only start time is set, set end time to next slot
      validEndTime = dayjs(startTime, 'HH:mm').add(15, 'minute').format('HH:mm')
    }
    
    updateSlotOnCalendar({
      date: rosterSlot?.customDate,
      startTime,
      endTime: validEndTime,
    })

    setRosterSlot((prev) => ({
      ...prev,
      slotStartTime: startTime,
      slotEndTime: validEndTime,
    }))
  }

  function handleChangeRosterSlot(e) {
    genericHandleChange(e, setRosterSlot)
  }

  useEffect(() => {
    const hasRosterContext =
      dropDownKey === 'mentorId' &&
      rosterSlot?.chamberRoomId &&
      rosterSlot?.customDate &&
      rosterSlot?.slotStartTime &&
      rosterSlot?.slotEndTime &&
      Array.isArray(effectiveLegendItems) &&
      effectiveLegendItems.length > 0
    const shouldCheckAvailability = hasRosterContext && rosterSlot?.eventType

    if (!shouldCheckAvailability) {
      setAvailabilityByMentorId({})
      setAvailabilityError(
        hasRosterContext && !rosterSlot?.eventType
          ? 'Event type is missing for this slot. Please reopen it from the correct event tab.'
          : '',
      )
      setAvailabilityLoading(false)
      return undefined
    }

    const mentorIds = effectiveLegendItems
      .map((item) => getSidebarLegendValue(item))
      .filter(Boolean)

    if (mentorIds.length === 0) {
      setAvailabilityByMentorId({})
      setAvailabilityError('')
      setAvailabilityLoading(false)
      return undefined
    }

    let cancelled = false
    setAvailabilityLoading(true)
    setAvailabilityError('')

    getRosterPractitionerAvailability({
      clinicBrandId: rosterSlot.clinicBrandId,
      clinicLocationId: rosterSlot.clinicLocationId,
      chamberRoomId: rosterSlot.chamberRoomId,
      eventType: rosterSlot.eventType,
      eventSubType: rosterSlot.eventSubType,
      excludeRosterSlotId: editingRosterSlot?.id || rosterSlot.excludeRosterSlotId,
      customDate: rosterSlot.customDate,
      slotStartTime: rosterSlot.slotStartTime,
      slotEndTime: rosterSlot.slotEndTime,
      mentorIds,
    })
      .then((resp) => {
        if (cancelled) return
        const availabilityResponse = getAvailabilityFromResponse(resp)
        const practitioners = availabilityResponse?.practitioners || []
        setAvailabilityByMentorId(
          practitioners.reduce((acc, practitioner) => {
            if (practitioner?.mentorId) {
              acc[practitioner.mentorId] = practitioner
            }
            return acc
          }, {}),
        )
        setAvailabilityError(
          practitioners.length > 0 ? '' : getResponseMessage(resp),
        )
      })
      .catch(() => {
        if (cancelled) return
        setAvailabilityByMentorId({})
        setAvailabilityError('Could not check practitioner availability. The server will still validate on save.')
      })
      .finally(() => {
        if (!cancelled) {
          setAvailabilityLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [
    dropDownKey,
    editingRosterSlot?.id,
    effectiveLegendItems,
    getSidebarLegendValue,
    rosterSlot,
    availabilityRefreshCounter,
  ])

  useEffect(() => {
    // Only update if selectedSlot actually changed (by reference or key fields)
    // This prevents resetting user input when they're typing
    setRosterSlot((prevSlot) => {
      if (!selectedSlot) {
        return selectedSlot
      }
      const hasChanged =
        !prevSlot ||
        selectedSlot.chamberRoomId !== prevSlot.chamberRoomId ||
        selectedSlot.customDate !== prevSlot.customDate ||
        (selectedSlot.slotStartTime !== prevSlot.slotStartTime && !prevSlot.slotStartTime) // Only reset if current is empty

      return hasChanged ? selectedSlot : prevSlot
    })
  }, [selectedSlot])

  function renderAvailabilityBadge(availability, { compact = false } = {}) {
    if (availabilityLoading && !availability) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
          <CircularProgress size={compact ? 13 : 15} />
          <Typography variant="caption">Checking</Typography>
        </Box>
      )
    }

    const config = getAvailabilityConfig(availability)
    const StatusIcon = config.icon
    const canInspect = availability && availability.fullyAvailable === false

    if (canInspect) {
      return (
        <Tooltip title="View availability details">
          <IconButton
            size="small"
            onMouseDown={(event) => {
              event.preventDefault()
              event.stopPropagation()
              setAvailabilityDetail(availability)
            }}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              setAvailabilityDetail(availability)
            }}
            sx={{ color: config.tone, p: 0.25 }}
          >
            <StatusIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    }

    return (
      <Tooltip title={config.label}>
        <StatusIcon sx={{ color: config.tone, fontSize: compact ? 17 : 19 }} />
      </Tooltip>
    )
  }

  function renderSelectedAvailabilityNotice() {
    if (availabilityError) {
      return (
        <Alert severity="warning" sx={{ mt: 1.5, py: 0.75 }}>
          {availabilityError}
        </Alert>
      )
    }
    if (!selectedAvailability) {
      return null
    }
    const config = getAvailabilityConfig(selectedAvailability)
    return (
      <Box
        role={selectedAvailability.fullyAvailable ? undefined : 'button'}
        tabIndex={selectedAvailability.fullyAvailable ? undefined : 0}
        onClick={() => {
          if (!selectedAvailability.fullyAvailable) {
            setAvailabilityDetail(selectedAvailability)
          }
        }}
        onKeyDown={(event) => {
          if (!selectedAvailability.fullyAvailable && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault()
            setAvailabilityDetail(selectedAvailability)
          }
        }}
        sx={{
          mt: 1.5,
          p: 1.25,
          borderRadius: 2,
          border: '1px solid',
          borderColor: selectedAvailability.fullyAvailable ? 'success.light' : `${config.color}.light`,
          bgcolor: selectedAvailability.fullyAvailable ? 'rgba(46, 125, 50, 0.08)' : 'background.default',
          cursor: selectedAvailability.fullyAvailable ? 'default' : 'pointer',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          {renderAvailabilityBadge(selectedAvailability)}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: config.tone }}>
              {config.label}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.35 }}>
              {selectedAvailability.summary}
            </Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  function renderRosterAssignmentError(resp) {
    const conflict = getAssignmentConflictFromResponse(resp)
    const message = getResponseMessage(resp) || getMessageMapFromCommonResponse(resp)
    if (!conflict) {
      return message
    }

    return (
      <Box sx={{ maxWidth: 520 }}>
        <Typography sx={{ fontWeight: 700, mb: 0.75 }}>
          Practitioner cannot be assigned
        </Typography>
        <Typography sx={{ fontSize: '0.88rem', mb: 1 }}>
          {message}
        </Typography>
        <Box sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.9)', display: 'grid', gap: 0.35 }}>
          <span>Slot: {formatSlotLabel(conflict)}</span>
          <span>
            Clinic: {joinVisible([conflict.clinicBrandName, conflict.clinicLocationName, conflict.chamberRoomName])}
          </span>
          <span>Status: {getAvailabilityConfig(conflict).label}</span>
        </Box>
        {conflict.engagements?.length > 0 && (
          <Box sx={{ mt: 1, display: 'grid', gap: 0.5 }}>
            {conflict.engagements.slice(0, 3).map((engagement) => (
              <Box key={`${engagement.type}-${engagement.id || engagement.message}`} sx={{ fontSize: '0.78rem' }}>
                {engagement.title || formatEventTypeLabel(engagement.type)} {engagement.startTime}-{engagement.endTime}
                {engagement.clinicLocationName ? ` at ${engagement.clinicLocationName}` : ''}
                {engagement.bookingStatus ? ` | booking: ${engagement.bookingStatus}` : ''}
                {engagement.paymentStatus ? ` | payment: ${engagement.paymentStatus}` : ''}
                {engagement.sessionStatus ? ` | session: ${engagement.sessionStatus}` : ''}
                {engagement.status && !engagement.bookingStatus ? ` | status: ${engagement.status}` : ''}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <>
      <DialogContent sx={{ p: 3, '&.MuiDialogContent-root': { pt: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <AccessTime sx={{ color: 'primary.main', fontSize: '1.5rem' }} />
            <Box>
              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                Date
              </Box>
              <Box sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
                {currentDateInDayJs.format('dddd, D MMMM YYYY')}
              </Box>
            </Box>
          </Box>
          {editingRosterSlot && (
            <Alert
              severity="info"
              sx={{ mb: 2, py: 0.75 }}
              action={
                <Button size="small" onClick={clearRosterSlotEdit}>
                  Cancel edit
                </Button>
              }
            >
              Editing roster slot {formatSlotLabel(editingRosterSlot)} at{' '}
              {joinVisible([
                editingRosterSlot.clinicBrandName,
                editingRosterSlot.clinicLocationName,
                editingRosterSlot.chamberRoomName,
              ])}
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
              Time Range
            </Box>
            <TimeRangePicker
              value={[rosterSlot?.slotStartTime, rosterSlot?.slotEndTime]}
              onChange={handleChangeDate}
              valueFormat="HH:mm"
              labelFormat="HH:mm"
              timeOptions={timeOptions}
              toTimeOptions={toTimeOptions}
              validateCustomTime={validateCustomTime}
            />
          </Box>
          <Box>
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
              {dropDownLabel}
            </Box>
            <CustomSelect
              id={dropDownKey}
              value={rosterSlot?.[dropDownKey]}
              options={enrichedLegendItems}
              getOptionValue={getSidebarLegendValue}
              getOptionLabel={getSidebarLegendLabel}
              label={dropDownLabel}
              onChangeEvent={handleChangeRosterSlot}
              selectType={selectTypes.string}
              menuPortalTarget={document.body}
              menuPlacement="auto"
              formatOptionLabel={(option, { context }) => {
                const label = getSidebarLegendLabel(option)
                const profilePicId = option?.profilePicId
                const profilePicUrl = profilePicId ? getImageLink(profilePicId) : null
                const availability = option?.availability
                const availabilityConfig = getAvailabilityConfig(availability)
                
                if (context === 'menu') {
                  // In dropdown menu - show with avatar
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.35, minWidth: 0 }}>
                      <Avatar
                        src={profilePicUrl}
                        sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
                      >
                        {!profilePicUrl && label?.[0]?.toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: availabilityConfig.color === 'error' ? 'error.main' : 'text.primary' }} noWrap>
                            {label}
                          </Typography>
                          {dropDownKey === 'mentorId' && renderAvailabilityBadge(availability)}
                        </Box>
                        {dropDownKey === 'mentorId' && (
                          <Typography sx={{ fontSize: '0.72rem', color: availabilityConfig.color === 'error' ? 'error.main' : 'text.secondary' }} noWrap>
                            {availability?.summary || (availabilityLoading ? 'Checking this slot...' : 'Availability not checked yet')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )
                }
                // In selected value - show with avatar
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Avatar
                      src={profilePicUrl}
                      sx={{ width: 20, height: 20, fontSize: '0.7rem' }}
                    >
                      {!profilePicUrl && label?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
                    {dropDownKey === 'mentorId' && renderAvailabilityBadge(availability, { compact: true })}
                  </Box>
                )
              }}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base) => ({
                  ...base,
                  borderRadius: '8px',
                  borderColor: '#e0e0e0',
                  '&:hover': {
                    borderColor: '#1976d2',
                  },
                }),
              }}
            />
            {dropDownKey === 'mentorId' && renderSelectedAvailabilityNotice()}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button 
          onClick={closePopup}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
        >
          Cancel
        </Button>
        <Button 
          disabled={!isRosterSlotValid || saving} 
          onClick={saveSlot} 
          variant="contained"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
            },
            '&:disabled': {
              opacity: 0.5,
            },
          }}
        >
          {saving ? 'Saving...' : editingRosterSlot ? 'Update slot' : 'Save'}
        </Button>
      </DialogActions>
      <Dialog
        open={!!availabilityDetail}
        onClose={() => setAvailabilityDetail(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Practitioner availability
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {availabilityDetail && (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                {renderAvailabilityBadge(availabilityDetail)}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700 }}>
                    {availabilityDetail.mentorName || 'Practitioner'}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                    {availabilityDetail.summary}
                  </Typography>
                </Box>
              </Box>

              <Box
                component="dl"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '120px minmax(0, 1fr)',
                  columnGap: 1.5,
                  rowGap: 0.75,
                  m: 0,
                  fontSize: '0.86rem',
                }}
              >
                <Typography component="dt" sx={{ color: 'text.secondary' }}>Slot</Typography>
                <Typography component="dd" sx={{ m: 0 }}>{formatSlotLabel(availabilityDetail)}</Typography>
                <Typography component="dt" sx={{ color: 'text.secondary' }}>Clinic</Typography>
                <Typography component="dd" sx={{ m: 0 }}>
                  {joinVisible([
                    availabilityDetail.clinicBrandName,
                    availabilityDetail.clinicLocationName,
                    availabilityDetail.chamberRoomName,
                  ])}
                </Typography>
                <Typography component="dt" sx={{ color: 'text.secondary' }}>Event type</Typography>
                <Typography component="dd" sx={{ m: 0 }}>
                  {formatEventTypeLabel(availabilityDetail.eventType)}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                  Engagements blocking this slot
                </Typography>
                {availabilityDetail.engagements?.length > 0 ? (
                  <Box sx={{ display: 'grid', gap: 1 }}>
                    {availabilityDetail.engagements.map((engagement) => {
                      const isRosterEngagement = engagement.type === 'roster-slot'
                      const isRecurringRoster = engagement?.metadata?.scheduleType === 'recurring'
                      const isActionLoading = slotActionLoadingId === engagement.id
                      return (
                        <Box
                          key={`${engagement.type}-${engagement.id || engagement.message}`}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            p: 1.25,
                            bgcolor: 'background.default',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                              {engagement.title || formatEventTypeLabel(engagement.type)}
                            </Typography>
                            <Chip
                              size="small"
                              color={engagement.conflictExtent === 'full' ? 'error' : 'warning'}
                              label={engagement.conflictExtent === 'full' ? 'Full overlap' : 'Partial overlap'}
                            />
                          </Box>
                          <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                            {formatSlotLabel(engagement)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                            {joinVisible([
                              engagement.clinicBrandName,
                              engagement.clinicLocationName,
                              engagement.chamberRoomName,
                            ])}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                            {engagement.status && <Chip size="small" variant="outlined" label={`Status: ${engagement.status}`} />}
                            {engagement.bookingStatus && <Chip size="small" variant="outlined" label={`Booking: ${engagement.bookingStatus}`} />}
                            {engagement.paymentStatus && <Chip size="small" variant="outlined" label={`Payment: ${engagement.paymentStatus}`} />}
                            {engagement.sessionStatus && <Chip size="small" variant="outlined" label={`Session: ${engagement.sessionStatus}`} />}
                            {engagement.eventType && <Chip size="small" variant="outlined" label={formatEventTypeLabel(engagement.eventType)} />}
                            {isRecurringRoster && <Chip size="small" variant="outlined" color="info" label="Recurring" />}
                          </Box>
                          {engagement.message && (
                            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.75 }}>
                              {engagement.message}
                            </Typography>
                          )}
                          {isRosterEngagement && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1, mt: 1.25 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditOutlinedIcon />}
                                disabled={isActionLoading || isRecurringRoster}
                                onClick={() => beginEditRosterEngagement(engagement)}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Edit slot
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={isActionLoading ? <CircularProgress size={14} /> : <EventBusyIcon />}
                                disabled={isActionLoading}
                                onClick={() => cancelRosterEngagement(engagement)}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                {isActionLoading ? 'Cancelling...' : 'Cancel slot'}
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                ) : (
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                    No blocking engagements were returned for this slot.
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvailabilityDetail(null)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
